/**
 * LBR2-D2 careers-page — candidate account (`/jobopenings/account`).
 *
 * Register / login against D1's `/public/careers/register|login`, then show the
 * signed-in candidate profile (from `/me`) + this-device application history.
 *
 * NOTE: D1's `/me` returns the candidate profile ONLY — there is no server-side
 * "list my applications" endpoint yet, so application history is device-local
 * (localStorage). Cross-device history needs a future candidate-scoped endpoint.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, LogOut, FileText } from 'lucide-react';
import { CareersChrome } from './CareersChrome';
import {
  registerCandidate,
  loginCandidate,
  fetchMe,
  clearCandidateToken,
  getCachedCandidate,
  getRecordedApplications,
  type Candidate,
} from './careersApi';

const inputCls =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20';

export function CandidateAccountPage() {
  const [candidate, setCandidate] = useState<Candidate | null>(getCachedCandidate());
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMe()
      .then((c) => {
        if (alive) {
          setCandidate(c);
          setReady(true);
        }
      })
      .catch(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!email.trim() || !password) {
      setErr('Email and password are required');
      return;
    }
    if (mode === 'register' && !fullName.trim()) {
      setErr('Full name is required');
      return;
    }
    if (mode === 'register' && password.length < 8) {
      setErr('Password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      const r =
        mode === 'register'
          ? await registerCandidate({
              email: email.trim(),
              password,
              fullName: fullName.trim(),
              phone: phone.trim() || undefined,
            })
          : await loginCandidate({ email: email.trim(), password });
      setCandidate(r.candidate);
    } catch (e2: any) {
      setErr(e2.message || 'Could not sign you in. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    clearCandidateToken();
    setCandidate(null);
    setEmail('');
    setPassword('');
  };

  if (candidate) {
    const apps = getRecordedApplications();
    return (
      <CareersChrome>
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="font-display text-xl font-bold text-gray-900">{candidate.fullName}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {candidate.email}
                  {candidate.phone ? ` · ${candidate.phone}` : ''}
                </p>
              </div>
              <button
                onClick={signOut}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-100"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="font-display text-lg font-bold text-gray-900">My applications</h2>
            {apps.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">
                No applications yet.{' '}
                <Link to="/jobopenings" className="font-semibold text-[#f5870a]">
                  Browse open positions
                </Link>
                .
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {apps.map((a) => (
                  <li
                    key={a.applicationId}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{a.jobTitle}</p>
                      <p className="text-xs text-gray-400">
                        Applied {new Date(a.appliedAt).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-gray-400">Application history is saved on this device.</p>
          </div>
        </div>
      </CareersChrome>
    );
  }

  return (
    <CareersChrome>
      <div className="mx-auto max-w-md">
        <div className="mb-4 flex rounded-xl bg-gray-100 p-1 text-sm font-semibold">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 rounded-lg py-2 ${mode === 'login' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
          >
            Sign in
          </button>
          <button
            onClick={() => setMode('register')}
            className={`flex-1 rounded-lg py-2 ${mode === 'register' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}
          >
            Create account
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {mode === 'register' && (
            <>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Full name *</label>
                <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-gray-700">Phone</label>
                <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" inputMode="tel" />
              </div>
            </>
          )}
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Email *</label>
            <input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" inputMode="email" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Password *</label>
            <input
              className={inputCls}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'register' ? 'At least 8 characters' : 'Your password'}
            />
          </div>
          {err && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !ready}
            className="w-full rounded-xl bg-[#f5870a] px-4 py-3 text-sm font-bold text-white shadow hover:bg-[#e07809] disabled:opacity-60"
          >
            {busy ? 'Please wait…' : mode === 'register' ? 'Create account' : 'Sign in'}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-500">
          <Link to="/jobopenings" className="font-semibold text-[#f5870a]">
            ← Back to open positions
          </Link>
        </p>
      </div>
    </CareersChrome>
  );
}

export default CandidateAccountPage;
